<script setup lang="ts">
/* eslint-disable vue/no-mutating-props */
// NOTE: This component intentionally mutates the user prop as part of its design.
// The parent component passes a buffer object that is expected to be modified in-place.
// We kept this approach when we refactored from vue-class-component to Composition API.
// @TODO: rework the entire state management to a more modern approach.
import { computed, onMounted, ref, shallowRef } from 'vue';
import { isEmpty, isNull } from 'lodash';
import { RestOperation, TradingPermissionOption } from '@/utils/helpers/rest';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import { FormValidator } from '@/utils/helpers/input-form';
import i18n from '@/localisation/i18n';
import { formatTradingPermissionOption } from '@/modules/broker-admin/utils';
import { getPriceAsString } from '@/utils/helpers/auction-numbers';
import Decimal from 'decimal.js';
import { UserRequest, UserResponse } from '@/modules/user-accounts/models';
import { TradingPermission } from '@/utils/helpers/trading-permissions';
import VueI18n from 'vue-i18n';
import { ApiError } from '@/utils/errors/api-error';
import axios, { AxiosError } from 'axios';
import logger from '@/modules/common/services/logger.service';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { useRouter } from '@/router/router';
import { store as vuex } from '@/store/store';

const props = defineProps<{
  crudAction: RestOperation;
  title: string;
  user: UserRequest;
  adminCount: number;
}>();

const emit = defineEmits<{
  (event: 'close-modal'): void;
}>();

const snackbar = useSnackbarManager();
const router = useRouter();

const cmpRoles = shallowRef(null);
const cmpTradingPermissions = shallowRef(null);
const cmpSoftLimitInput = shallowRef(null);
const cmpHardLimitInput = shallowRef(null);

const roles = [
  { text: i18n.tc('trader-user'), value: 'trader-user' },
  { text: i18n.tc('trader-viewer'), value: 'trader-viewer' },
  { text: i18n.tc('trader-admin'), value: 'trader-admin' },
  { text: i18n.tc('ops-user'), value: 'ops-user' },
  { text: i18n.tc('config-admin'), value: 'config-admin' },
];

const emailRules = [
  (v: string): true | VueI18n.TranslateResult =>
    /.+@.+/.test(v) || v === '' || i18n.t('formfield.InvalidEmail'),
];

const clErrors = ref<{ [index: string]: string }>({});
const apiErrors = ref<string[]>([]);
const tradingPermissionOptions = shallowRef<TradingPermissionOption[]>([]);
const allowedPermissions = shallowRef<TradingPermissionOption[]>([]);

const processing = ref(false);
const tabIndex = ref(0);
const lostAdminRole = ref(false);

const userOrderSoftLimit = shallowRef<Decimal | null>(null);
const userOrderHardLimit = shallowRef<Decimal | null>(null);
const softLimitTouched = ref(false);
const hardLimitTouched = ref(false);

const loginState = computed(() => vuex.state.loginState);
const hasTraderAdminRole = computed(() => vuex.getters.hasTraderAdminRole);

const effectiveTradingPermissions = computed<TradingPermission>(() => {
  if (props.user.tradingPermissions === null) {
    return props.user.companyTradingPermissions;
  }
  return props.user.companyTradingPermissions & props.user.tradingPermissions;
});

const isTraderViewer = computed(() => {
  return props.user.roles.length === 0 || props.user.roles.includes('trader-viewer');
});

const showError = computed(() => {
  return !!apiErrors.value.length;
});

// Soft limit validation
const softLimitErrors = computed<string[]>(() => {
  if (!isNull(userOrderSoftLimit.value) && softLimitTouched.value && !isTraderViewer.value) {
    if (userOrderSoftLimit.value.lessThan(1)) {
      return ['please enter a soft limit'];
    }
  }
  return [];
});

// Hard limit validation
const hardLimitErrors = computed<string[]>(() => {
  const errors: string[] = [];

  if (!isNull(userOrderHardLimit.value) && hardLimitTouched.value && !isTraderViewer.value) {
    if (userOrderHardLimit.value.lessThan(1)) {
      errors.push('please enter a hard limit');
    }

    // Check if hard limit is less than soft limit
    if (
      !isNull(userOrderSoftLimit.value) &&
      userOrderHardLimit.value.lessThan(userOrderSoftLimit.value)
    ) {
      errors.push('hard limit should always exceed the soft limit');
    }
  }

  return errors;
});

// softLimitMessage - what is the company default if we leave the field open?
const softLimitMessage = computed<string>(() => {
  return !isNull(userOrderSoftLimit.value) || isNull(props.user.defaultOrderSoftLimit)
    ? ''
    : `Inheriting company order soft limit value of $${getPriceAsString(
        props.user.defaultOrderSoftLimit,
        0
      )}`;
});

// hardLimitMessage - what is the company default if we leave the field open?
const hardLimitMessage = computed<string>(() => {
  return !isNull(userOrderHardLimit.value) || isNull(props.user.defaultOrderHardLimit)
    ? ''
    : `Inheriting company order hard limit value of $${getPriceAsString(
        props.user.defaultOrderHardLimit,
        0
      )}`;
});

function touchSoftLimit(): void {
  softLimitTouched.value = true;
}

function touchHardLimit(): void {
  hardLimitTouched.value = true;
}

function validateLimits(): boolean {
  if (isTraderViewer.value) {
    return true;
  }

  touchSoftLimit();
  touchHardLimit();

  if (softLimitErrors.value.length > 0 || hardLimitErrors.value.length > 0) {
    tabIndex.value = 1;
    return false;
  }

  return true;
}

// @TODO: we have this function duplicated in some components
// we should move to a service when we start fetching with connect-go
async function fetchTradingPermissionOptions(): Promise<void> {
  try {
    const { data } = await axios.get<{ options: TradingPermissionOption[] }>(
      '/api/1/trader/trading-permission-options'
    );
    tradingPermissionOptions.value = data.options;
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function createTraderAdminUser(user: UserRequest): Promise<void> {
  try {
    await axios.post(`/api/1/trader-admin/users`, UserRequest.toData(user));
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function updateTraderAdminUser(payload: {
  id: string;
  user: UserRequest;
  isAdministrator: boolean;
}): Promise<UserResponse> {
  try {
    const { data } = await axios.put(
      `/api/1/trader-admin/users/${payload.id}`,
      UserRequest.toData(payload.user),
      {
        params: {
          isAdministrator: payload.isAdministrator,
        },
      }
    );

    return UserResponse.fromData(data);
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

onMounted(async () => {
  try {
    await fetchTradingPermissionOptions();

    // add `null` as option for permissions for 'inherit from company'
    allowedPermissions.value = tradingPermissionOptions.value.concat({ permission: null });
    userOrderSoftLimit.value = props.user.orderSoftLimit;
    userOrderHardLimit.value = props.user.orderHardLimit;
  } catch (e) {
    logger.warn(e);
  }
});

async function onSubmit(): Promise<void> {
  if (processing.value) {
    return;
  }
  processing.value = true;

  try {
    // validate 'profile' tab
    clErrors.value = new FormValidator(props.user, ['name', 'emailAddress', 'roles']).check();
    if (!isEmpty(clErrors.value)) {
      tabIndex.value = 0;
      return;
    }

    apiErrors.value = [];
    if (props.user.id === loginState.value.user?.id) {
      if (!hasTraderAdminRole.value) {
        // current user lost admin rights
        if (props.adminCount === 1) {
          // not allowed if it is the only admin
          apiErrors.value.push(i18n.t('adminChangeLastAdminAccount') as string);
          tabIndex.value = 0;
          return;
        } else {
          // allowed (more than 1 admin) but this user cannot manage users anymore
          lostAdminRole.value = true;
        }
      }
    }

    if (!validateLimits()) {
      return;
    }

    // if updating a user with pending role approvals, they should be submitted as roles or
    // they will be reset.
    if (
      props.user.accountStatus === 'pending-approval-for-roles' &&
      props.user.rolesPendingApproval !== null
    ) {
      props.user.roles = props.user.rolesPendingApproval;
    }

    // clear risk-limits for read-only users
    //   limits were not validated because we should not error on a disabled tab
    if (isTraderViewer.value) {
      userOrderSoftLimit.value = null;
      userOrderHardLimit.value = null;
    }

    props.user.orderSoftLimit = userOrderSoftLimit.value;
    props.user.orderHardLimit = userOrderHardLimit.value;
    await doSubmit(props.user);
  } finally {
    processing.value = false;
  }
}

async function doSubmit(userRecord: UserRequest): Promise<void> {
  try {
    if (props.crudAction === RestOperation.Create) {
      await createTraderAdminUser(userRecord);
    } else {
      const updateResponse = await updateTraderAdminUser({
        id: props.user.id,
        user: userRecord,
        isAdministrator: !lostAdminRole.value,
      });

      if (updateResponse.approvalRequired) {
        snackbar.confirm(i18n.tc('adminUpdateUserApprovalRequired'));
      }
    }

    if (lostAdminRole.value) {
      // jump to the generic settings page if you are no longer admin
      await router.replace({ name: 'settings' });
      snackbar.confirm('You are no longer administrator.');
    }
    closeModalDialog();
  } catch (err) {
    apiErrors.value = [`${err}`];
  }
}

function closeModalDialog(): void {
  // allow parent to clean up
  emit('close-modal');
}

function formatTradingPermissionsLabel(option: TradingPermissionOption): string {
  return i18n.tc(formatTradingPermissionOption(option));
}
</script>

<!-- eslint-disable vue/no-mutating-props -->
<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="800px"
    no-click-animation
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
  >
    <VForm novalidate @submit.prevent>
      <VCard>
        <VCardTitle>
          <span class="headline">{{ $t(title) }}</span>
          <VSpacer></VSpacer>
          <span class="text--primary headline-2">{{ user.name }}</span>
          <span v-if="user.name && user.emailAddress" class="text--primary headline-2"> | </span>
          <span class="text--primary text-lowercase headline-2">{{ user.emailAddress }}</span>
        </VCardTitle>
        <VCardText>
          <VContainer v-if="showError">
            <VAlert dense type="error">{{ apiErrors.join('\n') }}</VAlert>
          </VContainer>
          <VTabs v-model="tabIndex">
            <VTab>Profile</VTab>
            <VTab :disabled="isTraderViewer">Risk Limits</VTab>
          </VTabs>

          <VTabsItems v-model="tabIndex">
            <VTabItem class="user-tab pa-4">
              <VContainer>
                <VRow>
                  <VCol>
                    <VTextField
                      v-model="user.emailAddress"
                      autocomplete="email"
                      autofocus
                      :error-messages="clErrors.emailAddress"
                      :label="$t('loginEmail')"
                      required
                      :rules="emailRules"
                    ></VTextField>
                  </VCol>
                </VRow>
                <VRow v-if="props.crudAction === RestOperation.Create">
                  <VCol>
                    <VAlert border="left" dense type="info">
                      The user will receive an invitation by email to activate the account and set a
                      password.
                    </VAlert>
                  </VCol>
                </VRow>
                <VRow>
                  <VCol>
                    <VTextField
                      v-model="user.name"
                      autocomplete="username"
                      :error-messages="clErrors.name"
                      :label="$t('Full Name')"
                      placeholder="e.g. Susan Smith"
                      required
                    ></VTextField>
                  </VCol>
                </VRow>

                <VRow>
                  <VCol>
                    <!-- If there are roles pending approval, we show the pending role not the actual role in the role
                      dropdown. This way the trader-admin can cancel the pending role assignment or switch it to a
                      different role assignment -->
                    <VSelect
                      v-if="
                        user.accountStatus === 'pending-approval-for-roles' &&
                        user.rolesPendingApproval
                      "
                      :ref="(r) => (cmpRoles = r)"
                      v-model="user.rolesPendingApproval[0]"
                      :error-messages="clErrors.roles"
                      :items="roles"
                      :label="$t('Role')"
                    />
                    <VSelect
                      v-else
                      :ref="(r) => (cmpRoles = r)"
                      v-model="user.roles[0]"
                      :error-messages="clErrors.roles"
                      :items="roles"
                      :label="$t('Role')"
                    />
                  </VCol>
                </VRow>
                <VRow>
                  <VCol>
                    <VSelect
                      :ref="(r) => (cmpTradingPermissions = r)"
                      v-model="user.tradingPermissions"
                      :error-messages="clErrors.tradingPermissions"
                      :item-text="formatTradingPermissionsLabel"
                      item-value="permission"
                      :items="allowedPermissions"
                      :label="$t(`tradingPermissions.title`)"
                    ></VSelect>
                  </VCol>
                </VRow>
                <VRow v-if="user.tradingPermissions === null">
                  <VCol cols="12">
                    <VAlert border="left" dense icon="mdi-information" type="info">
                      <span
                        >{{ $t('tradingPermissions.title') }} for the company are:
                        <em>
                          {{ $t(`tradingPermissions.options.${user.companyTradingPermissions}`) }}
                        </em>
                      </span>
                    </VAlert>
                  </VCol>
                </VRow>
                <VRow
                  v-else-if="
                    // no need to explain 'disabled'
                    user.tradingPermissions !== 0 &&
                    // and no need to explain when the permissions are the same
                    effectiveTradingPermissions !== user.tradingPermissions
                  "
                >
                  <VCol cols="12">
                    <VAlert border="left" dense icon="mdi-information" type="info">
                      <div>User permissions may be limited by permissions set on the company.</div>
                      <div>
                        Trading permissions for {{ user.companyName }} are:
                        <em>
                          {{ $t(`tradingPermissions.options.${user.companyTradingPermissions}`) }}.
                        </em>
                      </div>
                      <div>
                        As a result, the effective trading permissions of this user are:
                        <em>
                          {{ $t(`tradingPermissions.options.${effectiveTradingPermissions}`) }}.
                        </em>
                      </div>
                    </VAlert>
                  </VCol>
                </VRow>

                <VRow v-if="props.crudAction === RestOperation.Update">
                  <VCol>
                    <VCheckbox
                      v-model="user.resetPassword"
                      label="Send a Reset Password Link to the email address above"
                    >
                    </VCheckbox>
                  </VCol>
                </VRow>
              </VContainer>
            </VTabItem>
            <VTabItem class="user-tab pa-4">
              <VContainer>
                <VRow dense>
                  <VCol class="pt-6 pb-0 subtitle-1"> Per Order Soft Limit</VCol>
                </VRow>
                <VRow dense>
                  <VCol>
                    Fat-finger warning will trigger when {{ user.name }} attempts to enter an order
                    exceeding this notional value.
                  </VCol>
                </VRow>
                <VRow align="center" dense>
                  <VCol>Individual Per Order Soft Limit:</VCol>
                  <VCol>
                    <NumericInput
                      :ref="(r) => (cmpSoftLimitInput = r)"
                      v-model="userOrderSoftLimit"
                      :clearable="true"
                      :error-messages="softLimitErrors"
                      :messages="softLimitMessage"
                      :min="1"
                      :precision="0"
                      prefix="$"
                      type="decimal"
                      @blur="touchSoftLimit"
                      @input="touchSoftLimit"
                    />
                  </VCol>
                </VRow>
              </VContainer>
              <VContainer>
                <VRow dense>
                  <VCol class="pt-12 pb-0 subtitle-1"> Per Order Hard Limit</VCol>
                </VRow>
                <VRow dense>
                  <VCol>
                    Hard limit will be enforced when {{ user.name }} attempts to enter an order
                    exceeding this notional value, preventing order entry.
                  </VCol>
                </VRow>
                <VRow align="center" dense>
                  <VCol>Individual Per Order Hard Limit:</VCol>
                  <VCol>
                    <NumericInput
                      :ref="(r) => (cmpHardLimitInput = r)"
                      v-model="userOrderHardLimit"
                      :clearable="true"
                      :error-messages="hardLimitErrors"
                      :messages="hardLimitMessage"
                      :min="1"
                      :precision="0"
                      prefix="$"
                      type="decimal"
                      @blur="touchHardLimit"
                      @input="touchHardLimit"
                    />
                  </VCol>
                </VRow>
              </VContainer>
            </VTabItem>
          </VTabsItems>
        </VCardText>

        <VCardActions class="pt-16">
          <VBtn class="d-none d-sm-flex" color="secondary" text @click="closeModalDialog">
            {{ $t('cancelButton') }}
          </VBtn>
          <VSpacer></VSpacer>
          <VBtn
            color="primary"
            :disabled="processing"
            :loading="processing"
            min-width="120"
            type="submit"
            @click="onSubmit"
          >
            {{ $t('adminSaveUser') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>

<style lang="scss" scoped>
::v-deep {
  .user-tab {
    min-height: 575px;
  }
}
</style>
